/**
 * The single HTTP client for the whole app.
 *
 * The admin console's copy. Identical to ERP-FRONTEND's, with one difference
 * in how it is USED: an administrator has no "selected organization", so
 * `X-Tenant-Id` is passed explicitly per call when a platform endpoint needs
 * one, rather than being read from a stored selection.
 *
 * Every network call goes through here, which is what makes these behaviours
 * automatic rather than something each screen has to remember:
 *
 *  - the Authorization header, from the in-memory access token;
 *  - `X-Tenant-Id`, when a caller passes one;
 *  - transparent refresh on a 401, with concurrent 401s sharing ONE refresh
 *    call rather than each firing their own (which would trip the server's
 *    token-reuse detection and sign everyone out);
 *  - unwrapping the { success, data } envelope;
 *  - turning an error envelope into a typed ApiError carrying the machine
 *    code, the field-level details and the request id.
 *
 * The base URL is the API GATEWAY. The browser never learns an internal
 * service URL.
 */
import { clearTokens, getAccessToken, getRefreshToken, getTenantId, setTokens } from './tokenStore.js';

const BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/$/, '');

/** Endpoints that must never trigger a refresh attempt. */
const NO_REFRESH = [
  '/auth/login',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

export class ApiError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  /** Zod field errors, keyed by field, for inline form messages. */
  get fieldErrors() {
    if (!Array.isArray(this.details)) return {};
    return this.details.reduce((map, issue) => {
      if (issue?.field && !map[issue.field]) map[issue.field] = issue.message;
      return map;
    }, {});
  }

  get isValidation() {
    return this.code === 'VALIDATION_ERROR' || this.status === 422;
  }
  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isConflict() {
    return this.status === 409;
  }
}

/** Raised when the session cannot be recovered; the app signs the user out. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super('Your session has expired. Please sign in again.', { status: 401, code: 'SESSION_EXPIRED' });
    this.name = 'SessionExpiredError';
  }
}

// --- shared refresh --------------------------------------------------------
// A page load can fire several requests at once. If each retried its own
// refresh, the second would present an already-rotated token, which the server
// correctly treats as theft and answers by revoking every session. So all
// waiters share one in-flight refresh.
let refreshInFlight = null;
const sessionExpiredHandlers = new Set();

/** Lets AuthContext react to an unrecoverable session. */
export function onSessionExpired(handler) {
  sessionExpiredHandlers.add(handler);
  return () => sessionExpiredHandlers.delete(handler);
}

function announceSessionExpired() {
  clearTokens();
  for (const handler of sessionExpiredHandlers) handler();
}

async function refreshSession() {
  if (refreshInFlight) return refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    announceSessionExpired();
    throw new SessionExpiredError();
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success) {
        announceSessionExpired();
        throw new SessionExpiredError();
      }

      setTokens({ accessToken: payload.data.accessToken, refreshToken: payload.data.refreshToken });
      return payload.data.accessToken;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

function buildHeaders({ body, tenantId, skipTenant }) {
  const headers = {};

  if (body !== undefined) headers['content-type'] = 'application/json';

  const token = getAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  // The organization the user is working in. Untrusted by the server, which
  // revalidates it against their memberships on every request.
  const tenant = tenantId ?? (skipTenant ? null : getTenantId());
  if (tenant) headers['x-tenant-id'] = tenant;

  return headers;
}

async function parse(response) {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // A non-JSON body means something in front of the API answered (a proxy,
    // a tunnel, a captive portal). Surface it cleanly rather than crashing.
    throw new ApiError('The server returned an unexpected response', {
      status: response.status,
      code: 'BAD_RESPONSE',
    });
  }
}

function toError(response, payload) {
  const error = payload?.error ?? {};
  const message =
    error.message ??
    (response.status >= 500
      ? 'Something went wrong on our side. Please try again.'
      : 'The request could not be completed.');

  return new ApiError(message, {
    status: response.status,
    code: error.code,
    details: error.details,
    requestId: payload?.requestId,
  });
}

/**
 * @param {string} path e.g. '/purchase/suppliers'
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {object} [options.body]        serialised as JSON
 * @param {object} [options.query]       appended, skipping empty values
 * @param {string} [options.tenantId]    overrides the selected organization
 * @param {boolean} [options.skipTenant] omit X-Tenant-Id entirely
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.raw]        return the whole envelope, for pagination
 */
export async function apiRequest(path, options = {}) {
  const { method = 'GET', body, query, tenantId, skipTenant, signal, raw = false, _retried = false } = options;

  const url = new URL(`${BASE_URL}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders({ body, tenantId, skipTenant }),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    // The gateway is unreachable, or the browser is offline.
    throw new ApiError('Cannot reach the server. Check your connection and try again.', {
      status: 0,
      code: 'NETWORK_ERROR',
    });
  }

  const payload = await parse(response);

  if (response.ok) return raw ? payload : payload?.data ?? null;

  const error = toError(response, payload);

  // A 401 carries two entirely different meanings, and telling them apart is
  // the difference between a useful message and a user being thrown out.
  //
  //  - INVALID_CREDENTIALS means a password IN THE REQUEST BODY was wrong.
  //    The session is perfectly healthy. Refreshing it, replaying the same
  //    wrong password and then concluding the session is dead is what used to
  //    happen on /auth/change-password: mistyping the temporary password
  //    signed the user out mid-onboarding, and the screen unmounted before it
  //    could render the reason. So: no refresh, no retry, no sign-out - just
  //    surface it and let the form show it.
  //
  //  - anything else means the ACCESS TOKEN was rejected, which is exactly
  //    what a refresh-and-retry is for.
  if (error.status === 401 && error.code !== 'INVALID_CREDENTIALS' && !NO_REFRESH.includes(path)) {
    if (!_retried && getRefreshToken()) {
      await refreshSession();
      return apiRequest(path, { ...options, _retried: true });
    }
    announceSessionExpired();
  }

  throw error;
}

// --- in-flight GET coalescing ----------------------------------------------
// Two callers asking for the same thing at the same moment should cost ONE
// request, not two. That happens constantly: a list and the dropdown beside it,
// a context reloading while a screen mounts, and — every single time, in
// development — React StrictMode mounting each component twice.
//
// Only GETs are shared. Coalescing a POST would silently drop a second write.
//
// A caller's own AbortSignal still rejects THAT caller (useApi depends on it to
// discard a stale result); it simply no longer cancels a response another
// component is still waiting for.
const inFlightGets = new Map();

const abortError = () => new DOMException('The request was aborted', 'AbortError');

/** A stable key for a query object, so { a, b } and { b, a } match. */
function queryKey(query) {
  if (!query) return '';
  return Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key, String(value)])
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, value]) => key + '=' + value)
    .join('&');
}

function sharedGet(path, options = {}) {
  const { signal, ...rest } = options;

  // The tenant is part of the identity of a GET: the same path in another
  // organization is a different request and must never share a response.
  const tenant = rest.tenantId ?? (rest.skipTenant ? null : getTenantId());
  const key = JSON.stringify([path, queryKey(rest.query), tenant ?? null, Boolean(rest.raw)]);

  let shared = inFlightGets.get(key);
  if (!shared) {
    shared = apiRequest(path, { ...rest, method: 'GET' });
    inFlightGets.set(key, shared);
    // Cleared the moment it settles, so this is a coalescing window and not a
    // cache — the next call always goes to the network.
    shared.catch(() => {}).then(() => inFlightGets.delete(key));
  }

  if (!signal) return shared;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    shared.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
  });
}

export const api = {
  get: (path, options) => sharedGet(path, options),
  /** Returns the full envelope, so the caller can read `pagination`. */
  list: (path, query, options) => sharedGet(path, { ...options, query, raw: true }),
  post: (path, body, options) => apiRequest(path, { ...options, method: 'POST', body }),
  put: (path, body, options) => apiRequest(path, { ...options, method: 'PUT', body }),
  patch: (path, body, options) => apiRequest(path, { ...options, method: 'PATCH', body }),
  delete: (path, options) => apiRequest(path, { ...options, method: 'DELETE' }),
};

export const apiBaseUrl = BASE_URL;
